import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { IJobPostConfigProps } from '../interfaces';
import { useJobPostConfig } from '../hooks';
import { JobPostConfigHeader, StepAccordionItem } from '../components';
import {
  getSettingsSummary,
  getVideoSummary,
  getPrequalSummary,
  getCcaSummary,
} from '../functions';
import { STEP_METADATA } from '../constants';

// Step view child components
import JobPostSettings from '../../JobPostSettings';
import VideoMessageDetails from '../../VideoMessageDetails';
import PreQualifyingQuestions from '../../PreQualifyingQuestions';
import CCATab from '../../CCATab';

export const JobPostConfigContainer = ({
  jobTitleList,
  jobDescription,
  jobPostingData,
  setJobPostingData,
  setActiveTab,
  setJobPostId,
  jobPostId,
  orgPerksAndBenefits,
  orgCompanyDetails,
  hasCCALicense = false,
  assessmentInfo,
  setAssessmentInfo,
}: IJobPostConfigProps) => {
  const { t } = useTranslation(NS.adminPages);

  const {
    activeStep,
    setActiveStep,
    completionStatus,
    completedStepsCount,
    totalStepsCount,
    progressPercent,
    preQualQuestionsCount,
    ccaEnabled,
    ccaQuestionsCount,
    makeStepSetActiveTab,
  } = useJobPostConfig({
    jobPostingData,
    jobPostId,
    hasCCALicense,
    setActiveTab,
  });

  // Settings metadata badges
  const settingsBadges = useMemo(() => {
    const badges: string[] = [];
    if (jobPostingData?.location) badges.push(jobPostingData.location);
    if (jobPostingData?.workType) badges.push(jobPostingData.workType);
    if (jobPostingData?.department) badges.push(`Dept: ${jobPostingData.department}`);
    return badges;
  }, [jobPostingData]);

  // Video metadata badges
  const videoBadges = useMemo(() => {
    if (!jobPostingData?.enableVideo) return ['Disabled'];
    const badges = ['Enabled'];
    if (jobPostingData.videoTitle) badges.push(jobPostingData.videoTitle);
    return badges;
  }, [jobPostingData]);

  // Pre-qualifying metadata badges
  const prequalBadges = useMemo(() => {
    if (preQualQuestionsCount > 0) {
      return [`${preQualQuestionsCount} Question${preQualQuestionsCount > 1 ? 's' : ''}`];
    }
    return ['No questions added'];
  }, [preQualQuestionsCount]);

  // CCA metadata badges
  const ccaBadges = useMemo(() => {
    if (!ccaEnabled) return ['Disabled'];
    return ['Enabled', `${ccaQuestionsCount} Question${ccaQuestionsCount > 1 ? 's' : ''}`];
  }, [ccaEnabled, ccaQuestionsCount]);

  return (
    <Box sx={{ width: '100%', py: 3 }}>
      {/* Progress & Overview Header */}
      <Box sx={{ mb: 3 }}>
        <JobPostConfigHeader
          completedStepsCount={completedStepsCount}
          totalStepsCount={totalStepsCount}
          progressPercent={progressPercent}
        />
      </Box>

      {/* Accordion Steps List */}
      <Stack spacing={2.5}>
        {/* Step 1: Settings */}
        <StepAccordionItem
          stepId="settings"
          stepNumber={STEP_METADATA.settings.stepNumber}
          title={
            STEP_METADATA.settings.titleKey
              ? t(STEP_METADATA.settings.titleKey)
              : STEP_METADATA.settings.fallbackTitle
          }
          isActive={activeStep === 'settings'}
          isCompleted={completionStatus.settings}
          summaryText={getSettingsSummary(jobPostingData)}
          metadataBadges={settingsBadges}
          onSelect={() => setActiveStep('settings')}
        >
          <JobPostSettings
            jobTitleList={jobTitleList}
            jobPostingData={jobPostingData}
            setJobPostingData={setJobPostingData}
            setActiveTab={makeStepSetActiveTab('settings')}
            jobDescription={jobDescription}
            setJobPostId={setJobPostId}
          />
        </StepAccordionItem>

        {/* Step 2: Video Message */}
        <StepAccordionItem
          stepId="video"
          stepNumber={STEP_METADATA.video.stepNumber}
          title={
            STEP_METADATA.video.titleKey
              ? t(STEP_METADATA.video.titleKey)
              : STEP_METADATA.video.fallbackTitle
          }
          isActive={activeStep === 'video'}
          isCompleted={completionStatus.video}
          summaryText={getVideoSummary(jobPostingData)}
          metadataBadges={videoBadges}
          onSelect={() => setActiveStep('video')}
        >
          <VideoMessageDetails
            setActiveTab={makeStepSetActiveTab('video')}
            jobPostingData={jobPostingData}
            setJobPostingData={setJobPostingData}
          />
        </StepAccordionItem>

        {/* Step 3: Pre-Qualifying Questions */}
        <StepAccordionItem
          stepId="prequal"
          stepNumber={STEP_METADATA.prequal.stepNumber}
          title={
            STEP_METADATA.prequal.titleKey
              ? t(STEP_METADATA.prequal.titleKey)
              : STEP_METADATA.prequal.fallbackTitle
          }
          isActive={activeStep === 'prequal'}
          isCompleted={completionStatus.prequal}
          summaryText={getPrequalSummary(preQualQuestionsCount)}
          metadataBadges={prequalBadges}
          onSelect={() => setActiveStep('prequal')}
        >
          <PreQualifyingQuestions
            setActiveTab={makeStepSetActiveTab('prequal')}
            assessmentInfo={assessmentInfo}
            setAssessmentInfo={setAssessmentInfo}
            hasCCALicense={hasCCALicense}
          />
        </StepAccordionItem>

        {/* Step 4: CCA Questions (Conditional on hasCCALicense) */}
        {hasCCALicense && (
          <StepAccordionItem
            stepId="cca"
            stepNumber={STEP_METADATA.cca.stepNumber}
            title={STEP_METADATA.cca.fallbackTitle}
            isActive={activeStep === 'cca'}
            isCompleted={completionStatus.cca}
            summaryText={getCcaSummary(ccaEnabled, ccaQuestionsCount)}
            metadataBadges={ccaBadges}
            onSelect={() => setActiveStep('cca')}
          >
            <CCATab setActiveTab={makeStepSetActiveTab('cca')} />
          </StepAccordionItem>
        )}
      </Stack>
    </Box>
  );
};
